import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { patientAPI, doctorAPI } from '../services/api';
import { displayPatientCode } from '../utils/patientCode';

const EMPTY_FORM = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  bloodType: '', phone: '', email: '',
  emergencyContactName: '', emergencyContactPhone: '', emergencyRelationship: '',
  medicalHistory: '', allergies: '', currentMedications: '',
  status: 'ACTIVE',
};

export default function PatientFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form,    setForm]    = useState(EMPTY_FORM);
  const [doctors, setDoctors] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [patCode, setPatCode] = useState('');
  // Set only when the server generated a login password for this new patient.
  // While it is set the form stays put instead of navigating to the record, so
  // the navigator actually sees the credential — it cannot be retrieved later.
  const [created,  setCreated]  = useState(null);

  useEffect(() => {
    doctorAPI.list().then(r => setDoctors(Array.isArray(r.data) ? r.data : r.data?.content || [])).catch(() => setDoctors([]));
    if (isEdit) {
      patientAPI.get(id).then(r => {
        const p = r.data;
        setPatCode(displayPatientCode(p.patientCode));
        setForm({
          firstName:              p.firstName || '',
          lastName:               p.lastName || '',
          dateOfBirth:            p.dateOfBirth?.slice(0,10) || '',
          gender:                 p.gender || '',
          bloodType:              p.bloodType || '',
          phone:                  p.phone || '',
          email:                  p.email || '',
          emergencyContactName:   p.emergencyContactName || '',
          emergencyContactPhone:  p.emergencyContactPhone || '',
          emergencyRelationship:  p.emergencyRelationship || '',
          medicalHistory:         p.medicalHistory || '',
          allergies:              p.allergies || '',
          currentMedications:     p.currentMedications || '',
          status:                 p.status || 'ACTIVE',
        });
      }).catch(() => navigate('/patients'));
    }
  }, [id, isEdit, navigate]);

  const set = (k) => (e) => {
    const val = e.target.value;
    setFieldErrors(fe => (fe[k] ? { ...fe, [k]: undefined } : fe));
    setForm(f => ({
      ...f,
      [k]: val,

    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Mirror the backend's phone rule so the user is told which field is wrong
    // before the round trip: optional +, then 10-15 digits.
    const phoneOk = (v) => /^\+?[0-9]{10,15}$/.test((v || '').replace(/[\s-]/g, ''));
    const local = {};
    if (!phoneOk(form.phone)) local.phone = 'Enter a valid phone number (10–15 digits, optional leading +)';
    if (!phoneOk(form.emergencyContactPhone)) local.emergencyContactPhone = 'Enter a valid phone number (10–15 digits, optional leading +)';
    setFieldErrors(local);
    if (Object.keys(local).length) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await patientAPI.update(id, form);
        navigate(`/patients/${id}`);
      } else {
        const res = await patientAPI.create(form);
        const newId = res.data.id;
        const tempPassword = res.data?.temporaryPassword;
        if (tempPassword) {
          // Hold here and show it. The password is hashed on save and never
          // returned again, and the WhatsApp delivery that is supposed to carry
          // it is best-effort — it was failing silently against a template that
          // did not exist, which left patients with accounts they could not log
          // into. The navigator reading it out is the fallback.
          setCreated({ id: newId, tempPassword });
          setSaving(false);
          return;
        }
        navigate(`/patients/${newId}`);
      }
    } catch (err) {
      const fe = err.response?.data?.errors;
      if (fe && typeof fe === 'object') {
        setFieldErrors(fe);
        setError('Please fix the highlighted fields: ' + Object.values(fe).join('; '));
      } else {
        setError(err.response?.data?.message || 'Failed to save patient. Please check the form.');
      }
      setSaving(false);
    }
  };

  const breadcrumbs = isEdit
    ? [{ label: 'Patients', to: '/patients' }, { label: `${form.firstName} ${form.lastName}`, to: `/patients/${id}` }, { label: 'Edit' }]
    : [{ label: 'Patients', to: '/patients' }, { label: 'Add New Patient' }];

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      <span className="back-link fade-in-up" onClick={() => navigate(isEdit ? `/patients/${id}` : '/patients')}>
        ← {isEdit ? 'Back to Patient' : 'Back to Patients'}
      </span>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">{isEdit ? 'Edit Patient' : 'Add New Patient'}</div>
          <div className="page-subtitle">
            {isEdit ? `Editing record for ${form.firstName} ${form.lastName}` : 'Fill in all required fields to register a new patient'}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error fade-in-up">⚠️ {error}</div>}

      {created && (
        <div className="alert alert-warning fade-in-up" style={{ display: 'block' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Patient registered — temporary password
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 16, letterSpacing: '0.5px' }}>{created.tempPassword}</code>
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => navigator.clipboard?.writeText(created.tempPassword)}>
              Copy
            </button>
          </div>
          <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
            They log in with their phone number and this password, and must change
            it at first login. It is also sent on WhatsApp, but that delivery is
            not guaranteed — pass it on directly if they do not receive it. This is
            the only time it can be read; after this it has to be reset.
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary btn-sm"
              onClick={() => navigate(`/patients/${created.id}`)}>
              Continue to patient record →
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Personal Info */}
        <div className="form-section fade-in-up delay-1">
          <div className="form-section-title">👤 Personal Information</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">First Name <span className="required">*</span></label>
              <input className="form-input" value={form.firstName} onChange={set('firstName')} placeholder="e.g. Ravi" required />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span className="required">*</span></label>
              <input className="form-input" value={form.lastName} onChange={set('lastName')} placeholder="e.g. Krishnamurti" required />
            </div>
            <div className="form-group">
              <label className="form-label">Date of Birth <span className="required">*</span></label>
              <input className="form-input" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Gender <span className="required">*</span></label>
              <select className="form-select" value={form.gender} onChange={set('gender')} required>
                <option value="">Select gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Blood Group</label>
              <select className="form-select" value={form.bloodType} onChange={set('bloodType')}>
                <option value="">Select</option>
                {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={set('status')}>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            {isEdit && (
              <div className="form-group">
                <label className="form-label">Patient ID</label>
                <input className="form-input readonly" value={patCode} readOnly />
              </div>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="form-section fade-in-up delay-2">
          <div className="form-section-title">📞 Contact Details</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Mobile Number <span className="required">*</span></label>
              <input className={`form-input${fieldErrors.phone ? ' is-invalid' : ''}`} value={form.phone} onChange={set('phone')} placeholder="+91 XXXXX XXXXX" required />
              {fieldErrors.phone && <div className="form-error">{fieldErrors.phone}</div>}
            </div>
<div className="form-group">
              <label className="form-label">Email Address <span className="required">*</span></label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="patient@email.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">Emergency Contact Name <span className="required">*</span></label>
              <input className="form-input" value={form.emergencyContactName} onChange={set('emergencyContactName')} placeholder="Full name" required />
            </div>
            <div className="form-group">
              <label className="form-label">Emergency Contact Phone <span className="required">*</span></label>
              <input className={`form-input${fieldErrors.emergencyContactPhone ? ' is-invalid' : ''}`} value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} placeholder="+91 XXXXX XXXXX" required />
              {fieldErrors.emergencyContactPhone && <div className="form-error">{fieldErrors.emergencyContactPhone}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Relationship</label>
              <select className="form-select" value={form.emergencyRelationship} onChange={set('emergencyRelationship')}>
                <option value="">Select</option>
                {['Spouse','Parent','Child','Sibling','Friend','Other'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Medical */}
        <div className="form-section fade-in-up delay-3">
          <div className="form-section-title">💊 Medical Information</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Primary Diagnosis</label>
              <input className="form-input" value={form.medicalHistory} onChange={set('medicalHistory')} placeholder="e.g. Type 2 Diabetes Mellitus" />
            </div>
<div className="form-group form-full">
              <label className="form-label">Known Allergies</label>
              <input className="form-input" value={form.allergies} onChange={set('allergies')} placeholder="e.g. Penicillin, Aspirin (comma-separated)" />
            </div>
            <div className="form-group form-full">
              <label className="form-label">Current Medications</label>
              <input className="form-input" value={form.currentMedications} onChange={set('currentMedications')} placeholder="e.g. Metformin 500mg, Amlodipine 5mg" />
            </div>
          </div>
        </div>

        <div className="form-actions fade-in-up delay-4">
          <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? '✓ Update Patient' : '✓ Save Patient'}
          </button>
          {!isEdit && (
            <button className="btn btn-secondary" type="submit" disabled={saving}>
              Save & Add Address
            </button>
          )}
          <button className="btn btn-ghost" type="button" onClick={() => navigate(isEdit ? `/patients/${id}` : '/patients')}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
